import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import { track } from "../lib/analytics";
import { PageHeader } from "../shell/PageHeader";
import {
  CatalogSortSelect,
  DEFAULT_SORT,
  isCatalogSortValue,
  sortTypes,
  type CatalogSortValue
} from "./CatalogSortSelect";
import { EquipmentCard } from "./EquipmentCard";
import {
  useAllPublicTypes,
  usePublicCategories,
  usePublicTypeSearch
} from "./api";
import { buildSuggestions, type Suggestion } from "./search_suggestions";
import type { CategorySummary, TypeSummary } from "./types";

// FR-5 — /search?q=… overflow page.
//
// Phase 4i refinement landed inline search input + result count + sort
// dropdown + a richer zero-result CTA. Phase 4r (this slice) closes
// the rest of the design-audit gap:
//   • Category facet chips above the grid — multi-select, URL-persisted
//     as `?categories=slug-a,slug-b`. Counts are derived from the
//     unfiltered result set so toggling a chip never makes the other
//     chips disappear from under the cursor.
//   • "Did you mean…" suggestions on zero results — client-side
//     Levenshtein against the full catalog. Lazy: the all-types fetch
//     only fires when a query has no hits.
//   • Sparse-result grid tightening — ≤2 results render in a narrower
//     2-up layout so the page doesn't read as "two cards floating in
//     dead space" on wide viewports.
//   • Sticky inline search bar — stays in view while scrolling long
//     result lists so the refine path is always one keystroke away.
export function SearchResultsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo(
    () => searchParams.get("q")?.trim() ?? "",
    [searchParams]
  );
  const sortParam = searchParams.get("sort");
  const sort: CatalogSortValue = isCatalogSortValue(sortParam)
    ? sortParam
    : DEFAULT_SORT;
  const selectedCategorySlugs = useMemo<readonly string[]>(
    () => parseCategoryParam(searchParams.get("categories")),
    [searchParams]
  );

  const categoriesQuery = usePublicCategories();
  const { data: searchData, isPending, isError, error } = usePublicTypeSearch(query);

  const matchedTypes = searchData ?? [];
  const hasQuery = query.length > 0;
  const zeroMatches = hasQuery && !isPending && !isError && matchedTypes.length === 0;

  // "Did you mean" — only fetch the full catalog when we know there's
  // a zero-result query to suggest against. Keeps the happy path one
  // request lighter.
  const allTypesQuery = useAllPublicTypes({ enabled: zeroMatches });

  const suggestions = useMemo<Suggestion[]>(() => {
    if (!zeroMatches) return [];
    const types = allTypesQuery.data ?? [];
    const cats = categoriesQuery.data ?? [];
    if (types.length === 0 && cats.length === 0) return [];
    return buildSuggestions(query, types, cats);
  }, [zeroMatches, allTypesQuery.data, categoriesQuery.data, query]);

  // Facet chips reflect what *would* match without filtering, so they
  // never vanish under the user's cursor when toggled.
  const facets = useMemo(() => computeFacets(matchedTypes), [matchedTypes]);
  const filteredTypes = useMemo(
    () => filterByCategory(matchedTypes, selectedCategorySlugs),
    [matchedTypes, selectedCategorySlugs]
  );
  const sortedTypes = useMemo<TypeSummary[]>(
    () => sortTypes(filteredTypes, sort),
    [filteredTypes, sort]
  );

  useEffect(() => {
    if (query.length > 0) {
      track({
        name: "search_performed",
        query_length: query.length,
        result_count: matchedTypes.length
      });
    }
    // Only fire on query change + initial settled load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, matchedTypes.length]);

  function handleSortChange(next: CatalogSortValue): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === DEFAULT_SORT) {
          params.delete("sort");
        } else {
          params.set("sort", next);
        }
        return params;
      },
      { replace: true }
    );
  }

  function handleQuerySubmit(nextQuery: string): void {
    const trimmed = nextQuery.trim();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (trimmed.length === 0) {
        params.delete("q");
      } else {
        params.set("q", trimmed);
      }
      // A new query implies the prior category narrow may be stale —
      // clear it so the customer doesn't get an empty result set from
      // their own filters they've forgotten about.
      params.delete("categories");
      return params;
    });
  }

  function toggleCategory(slug: string): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        const current = parseCategoryParam(params.get("categories"));
        const next = current.includes(slug)
          ? current.filter((s) => s !== slug)
          : [...current, slug];
        if (next.length === 0) {
          params.delete("categories");
        } else {
          params.set("categories", next.join(","));
        }
        return params;
      },
      { replace: true }
    );
  }

  function clearCategoryFilters(): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("categories");
        return params;
      },
      { replace: true }
    );
  }

  const matchCount = matchedTypes.length;
  const visibleCount = sortedTypes.length;
  const isFiltering = selectedCategorySlugs.length > 0;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Search"
        title={hasQuery ? `Results for “${query}”` : "Search the fleet"}
        description={
          hasQuery
            ? "Refine your search below, or browse by category."
            : "Find equipment by name or category."
        }
      />

      <StickySearchBar>
        <InlineSearchInput initialQuery={query} onSubmit={handleQuerySubmit} />
      </StickySearchBar>

      {!hasQuery ? (
        <PreSearchEmptyState />
      ) : isPending ? (
        <SearchSkeleton />
      ) : isError ? (
        <Alert variant="destructive">
          Search failed. {error instanceof ApiError ? `(HTTP ${error.status})` : null}
        </Alert>
      ) : zeroMatches ? (
        <EmptyResults
          query={query}
          categories={categoriesQuery.data ?? []}
          suggestions={suggestions}
          suggestionsLoading={allTypesQuery.isPending}
        />
      ) : (
        <>
          {facets.length > 1 ? (
            <CategoryFacets
              facets={facets}
              selectedSlugs={selectedCategorySlugs}
              onToggle={toggleCategory}
              onClear={clearCategoryFilters}
            />
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-zinc-700">
              {summaryText(matchCount, visibleCount, query, isFiltering)}
            </p>
            <CatalogSortSelect value={sort} onChange={handleSortChange} />
          </div>

          {visibleCount === 0 ? (
            <FilteredOutEmptyState
              query={query}
              selectedSlugs={selectedCategorySlugs}
              onClear={clearCategoryFilters}
            />
          ) : (
            <ResultsGrid types={sortedTypes} />
          )}
        </>
      )}
    </div>
  );
}

function summaryText(
  matchCount: number,
  visibleCount: number,
  query: string,
  isFiltering: boolean
): string {
  const noun = (n: number) => (n === 1 ? "result" : "results");
  if (!isFiltering) {
    return `Showing ${matchCount} ${noun(matchCount)} for “${query}”`;
  }
  return `Showing ${visibleCount} of ${matchCount} ${noun(matchCount)} for “${query}”`;
}

interface CategoryFacet {
  slug: string;
  name: string;
  count: number;
}

function computeFacets(types: TypeSummary[]): CategoryFacet[] {
  const byCategory = new Map<string, CategoryFacet>();
  for (const t of types) {
    const cat = t.category;
    const existing = byCategory.get(cat.slug);
    if (existing) {
      existing.count += 1;
    } else {
      byCategory.set(cat.slug, { slug: cat.slug, name: cat.name, count: 1 });
    }
  }
  // Stable sort: most matches first, then alpha by name as tiebreaker
  // so the chip order doesn't bounce around when counts tie.
  return [...byCategory.values()].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.name.localeCompare(b.name);
  });
}

function filterByCategory(
  types: TypeSummary[],
  selectedSlugs: readonly string[]
): TypeSummary[] {
  if (selectedSlugs.length === 0) return types;
  const wanted = new Set(selectedSlugs);
  return types.filter((t) => wanted.has(t.category.slug));
}

function parseCategoryParam(raw: string | null): string[] {
  if (raw == null || raw.trim() === "") return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Stays at the top of the viewport while the customer scrolls past the
// fold. Sat right below the global nav, so the refine path is always
// one keystroke away on long result lists. The translucent background
// + backdrop-blur keeps the cards visible underneath without losing
// chrome contrast.
function StickySearchBar({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      className={cn(
        "sticky top-16 z-20 -mx-4 border-b border-zinc-200 bg-brand-white/90 px-4 py-3 backdrop-blur",
        "md:-mx-6 md:px-6"
      )}
    >
      {children}
    </div>
  );
}

function InlineSearchInput({
  initialQuery,
  onSubmit
}: {
  initialQuery: string;
  onSubmit: (query: string) => void;
}): JSX.Element {
  // Local input value is its own state so typing doesn't roundtrip
  // through the URL each keystroke; we only push to ?q= on submit.
  const [value, setValue] = useState(initialQuery);

  // Keep local state in sync if the URL changes from outside (back
  // button, deep-link from the header search palette, etc.).
  useEffect(() => {
    setValue(initialQuery);
  }, [initialQuery]);

  function onFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(value);
  }

  return (
    <form
      role="search"
      onSubmit={onFormSubmit}
      aria-label="Refine search"
      className={cn(
        "mx-auto flex w-full max-w-2xl items-center gap-2 rounded-lg border border-zinc-300 bg-brand-white",
        "px-3 py-2 shadow-sm focus-within:border-brand-red focus-within:ring-2 focus-within:ring-brand-red/30"
      )}
    >
      <span aria-hidden="true" className="text-zinc-400">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="20" y1="20" x2="16.6" y2="16.6" />
        </svg>
      </span>
      <input
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        aria-label="Search the fleet"
        placeholder="Search by name, model, or category"
        autoComplete="off"
        spellCheck={false}
        className="block w-full bg-transparent text-base text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
      />
      <button
        type="submit"
        className={cn(
          "rounded-md bg-brand-red px-3 py-1.5 text-sm font-semibold text-brand-white",
          "shadow-sm hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-brand-red/40"
        )}
      >
        Search
      </button>
    </form>
  );
}

function CategoryFacets({
  facets,
  selectedSlugs,
  onToggle,
  onClear
}: {
  facets: CategoryFacet[];
  selectedSlugs: readonly string[];
  onToggle: (slug: string) => void;
  onClear: () => void;
}): JSX.Element {
  const hasAnySelection = selectedSlugs.length > 0;
  return (
    <nav aria-label="Filter by category">
      <ul className="flex flex-wrap items-center gap-2">
        {facets.map((facet) => {
          const active = selectedSlugs.includes(facet.slug);
          return (
            <li key={facet.slug}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onToggle(facet.slug)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40",
                  active
                    ? "border border-brand-red bg-brand-red text-brand-white hover:bg-brand-red-dark"
                    : "border border-zinc-300 bg-brand-white text-zinc-700 hover:border-brand-red hover:text-brand-red"
                )}
                data-testid={`facet-${facet.slug}`}
              >
                <span>{facet.name}</span>
                <span
                  className={cn(
                    "rounded-full px-1.5 text-xs tabular-nums",
                    active ? "bg-brand-white/20 text-brand-white" : "bg-zinc-100 text-zinc-600"
                  )}
                >
                  {facet.count}
                </span>
              </button>
            </li>
          );
        })}
        {hasAnySelection ? (
          <li>
            <button
              type="button"
              onClick={onClear}
              className="text-sm font-medium text-brand-red underline-offset-2 hover:underline"
            >
              Clear filters
            </button>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}

function ResultsGrid({ types }: { types: TypeSummary[] }): JSX.Element {
  // Sparse-result tightening — when there are only one or two cards a
  // 4-column grid leaves them stranded in dead space on wide viewports.
  // We collapse to a narrower column count and a max-width container
  // so the cards feel anchored, not abandoned:
  //   • 1 result  → centered single column, capped at the card's natural
  //                 reading width so it doesn't stretch ear-to-ear.
  //   • 2 results → two-up at sm+; max-w-3xl keeps them paired tightly.
  //   • 3+ → the standard responsive grid.
  const cls = (() => {
    if (types.length === 1) return "mx-auto grid w-full max-w-md gap-4";
    if (types.length === 2) return "mx-auto grid w-full max-w-3xl gap-4 sm:grid-cols-2";
    return "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
  })();
  return (
    <ul className={cls}>
      {types.map((t) => (
        <li key={t.id}>
          {/* The card's "View details" link still uses /c/_/<type-slug>
              as a placeholder; TypeDetailPage rewrites the URL to the
              canonical /c/<actual-category>/<type> on load. */}
          <EquipmentCard type={t} categorySlug="_" />
        </li>
      ))}
    </ul>
  );
}

function FilteredOutEmptyState({
  query,
  selectedSlugs,
  onClear
}: {
  query: string;
  selectedSlugs: readonly string[];
  onClear: () => void;
}): JSX.Element {
  const filterCount = selectedSlugs.length;
  return (
    <section
      role="region"
      aria-label="Filters hid all matches"
      className="rounded-lg border border-dashed border-zinc-300 bg-brand-white p-8 text-center"
    >
      <p className="text-base font-semibold text-brand-black">
        Your filters hid every match for &ldquo;{query}&rdquo;.
      </p>
      <p className="mt-2 text-sm text-zinc-600">
        {filterCount === 1
          ? "Try clearing the category filter to see the full result set."
          : "Try clearing the category filters to see the full result set."}
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-4 inline-flex items-center rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white shadow-sm hover:bg-brand-red-dark"
      >
        Clear filters
      </button>
    </section>
  );
}

function EmptyResults({
  query,
  categories,
  suggestions,
  suggestionsLoading
}: {
  query: string;
  categories: CategorySummary[];
  suggestions: Suggestion[];
  suggestionsLoading: boolean;
}): JSX.Element {
  const quickJump = categories.slice(0, 3);
  return (
    <section
      role="region"
      aria-label="No matches"
      className="rounded-lg border border-dashed border-zinc-300 bg-brand-white p-10 text-center"
    >
      <p className="text-lg font-semibold text-brand-black">
        No equipment matched &ldquo;{query}&rdquo;.
      </p>

      {suggestionsLoading ? (
        <p className="mt-3 text-sm text-zinc-500" aria-live="polite">
          Looking for similar names…
        </p>
      ) : suggestions.length > 0 ? (
        <DidYouMean suggestions={suggestions} />
      ) : null}

      <p className="mt-4 text-sm text-zinc-600">
        If we don&rsquo;t carry it, give us a call &mdash; we can usually source what
        you need.
      </p>
      <p className="mt-5">
        <a
          href="tel:+13034354328"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-red px-4 py-2 text-sm font-semibold text-brand-white shadow-sm hover:bg-brand-red-dark"
        >
          Call 303-435-4328
        </a>
      </p>

      {quickJump.length > 0 ? (
        <>
          <p className="mt-6 text-sm text-zinc-600">Or try one of these categories:</p>
          <ul className="mt-3 flex flex-wrap justify-center gap-2">
            {quickJump.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/c/${c.slug}`}
                  className="inline-flex items-center rounded-md border border-zinc-300 bg-brand-white px-4 py-2 text-sm font-medium text-brand-black transition hover:border-brand-red hover:text-brand-red"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <p className="mt-6 text-sm">
        <Link
          to="/"
          className="font-semibold text-brand-red hover:text-brand-red-dark hover:underline"
        >
          Browse all equipment →
        </Link>
      </p>
    </section>
  );
}

function DidYouMean({ suggestions }: { suggestions: Suggestion[] }): JSX.Element {
  return (
    <p className="mt-4 text-sm text-zinc-700" data-testid="did-you-mean">
      Did you mean:{" "}
      {suggestions.map((s, idx) => (
        <span key={`${s.kind}-${s.label}`}>
          <Link
            to={s.href}
            className="font-semibold text-brand-red underline-offset-2 hover:underline"
          >
            {s.label}
          </Link>
          {idx < suggestions.length - 1
            ? idx === suggestions.length - 2
              ? ", or "
              : ", "
            : "?"}
        </span>
      ))}
    </p>
  );
}

function PreSearchEmptyState(): JSX.Element {
  return (
    <p className="text-center text-sm text-zinc-500">
      Try a model name (&ldquo;Bobcat S70&rdquo;) or a category (&ldquo;skid steer&rdquo;).
    </p>
  );
}

function SearchSkeleton(): JSX.Element {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-64" />
        </li>
      ))}
    </ul>
  );
}
